import type { PageDefinition } from '@/types/page'
import {
  CHANNEL_OPTIONS,
  CONTENT_STATUS,
  MESSAGE_STATUS,
  MESSAGE_TYPE_OPTIONS,
  TEMPLATE_STATUS,
  toOptions,
} from './common'

const TIP_CATEGORIES = ['饮食营养', '运动康复', '用药安全', '慢病管理', '心理健康'].map(value => ({ value, label: value }))
const AUDIENCES = ['全部用户', '高血压人群', '60岁以上', '高风险家庭', '糖尿病用户'].map(value => ({ value, label: value }))
const TEMPLATE_TYPES = ['健康提醒', '用药提醒', '会员通知', '系统公告', '服务关怀'].map(value => ({ value, label: value }))
const MESSAGE_VARIABLES = ['用户姓名', '提醒类型', '计划名称', '提醒时间', '家庭名称', '会员等级', '客服工单号'].map(value => ({ value, label: value }))
const FAQ_CATEGORIES = ['账号与认证', '用药提醒', '健康监测', '家庭守护', '会员服务'].map(value => ({ value, label: value }))

export const messagesPage: PageDefinition = {
  id: 'messages',
  route: '#/messages',
  title: '消息通知',
  purpose: '编排用药提醒、健康预警、会员权益与系统公告的受众、渠道、发送时间和送达结果',
  group: '消息通知',
  icon: 'bell',
  kind: 'table',
  permission: 'messages.view',
  permissions: {
    create: 'messages.create',
    send: 'messages.send',
    cancel: 'messages.cancel',
  },
  searchKeys: ['message_no', 'title', 'message_type', 'template_name', 'created_by'],
  statusKey: 'status',
  statusMap: MESSAGE_STATUS,
  timeField: 'created_at',
  summary: [
    { key: 'total', label: '消息总数', kind: 'count' },
    { key: 'sent', label: '已发送', kind: 'count', where: { status: 'sent' } },
    { key: 'scheduled', label: '待发送', kind: 'count', where: { status: 'scheduled' } },
    { key: 'failed', label: '发送失败', kind: 'count', where: { status: 'failed' } },
    { key: 'delivered', label: '累计送达', kind: 'sum', field: 'delivered', suffix: ' 人次' },
  ],
  columns: [
    { key: 'title', label: '消息标题', type: 'primary', subtitleKey: 'message_no', minWidth: 250, sortable: true },
    { key: 'message_type', label: '类型', type: 'tag', width: 96 },
    { key: 'channels', label: '发送渠道', type: 'tag', minWidth: 140 },
    { key: 'audience_type', label: '受众', width: 102 },
    { key: 'audience_label', label: '预计触达', width: 106 },
    { key: 'status', label: '状态', type: 'status', width: 92 },
    { key: 'scheduled_at', label: '计划发送', type: 'datetime', width: 156 },
    { key: 'delivered', label: '已送达', type: 'number', width: 82, align: 'right' },
    { key: 'created_by', label: '创建人', width: 82 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '消息标题、编号或模板', width: 240 },
    { key: 'message_type', label: '消息类型', type: 'select', options: MESSAGE_TYPE_OPTIONS, width: 120 },
    { key: 'status', label: '发送状态', type: 'select', options: toOptions(MESSAGE_STATUS), width: 116 },
    { key: 'channel', label: '发送渠道', type: 'select', options: CHANNEL_OPTIONS, width: 120 },
    { key: 'created_at', label: '创建时间', type: 'daterange', timeField: 'created_at', width: 230 },
  ],
  formFields: [
    { key: 'message_no', label: '消息编号', type: 'readonly', readonlyOnEdit: true, help: '保存后由系统生成，不可修改', placeholder: '保存后自动生成' },
    { key: 'title', label: '消息标题', type: 'text', required: true, maxLength: 50, placeholder: '用户端通知列表展示的标题' },
    { key: 'message_type', label: '消息类型', type: 'select', options: MESSAGE_TYPE_OPTIONS, required: true },
    { key: 'template_name', label: '通知模板', type: 'select', optionSource: 'notification_templates', required: true, help: '模板变量会按受众数据自动替换' },
    { key: 'channels', label: '发送渠道', type: 'multiselect', options: CHANNEL_OPTIONS, required: true },
    { key: 'audience_type', label: '受众范围', type: 'select', options: ['全部用户', '指定标签', '指定家庭', '指定用户'].map(value => ({ value, label: value })), required: true },
    { key: 'audience_label', label: '受众说明', type: 'text', required: true, maxLength: 30, placeholder: '如：高血压人群 1,280 人' },
    { key: 'scheduled_at', label: '计划发送时间', type: 'datetime', required: true },
    { key: 'content', label: '消息内容', type: 'textarea', required: true, full: true, maxLength: 500, placeholder: '支持 {{用户姓名}} 等模板变量' },
    { key: 'status', label: '保存状态', type: 'select', options: [
      { value: 'draft', label: '保存为草稿' },
      { value: 'scheduled', label: '加入发送队列' },
    ], defaultValue: 'draft' },
  ],
  createLabel: '新建通知',
  createPermission: 'messages.create',
  editPermission: 'messages.create',
  exportScope: 'none',
  extraActions: [
    { key: 'templates', label: '通知模板', icon: 'layers', kind: 'link', to: '#/messages/templates', permission: 'templates.view' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'messages.create', tone: 'primary', visibleWhen: { field: 'status', in: ['draft', 'scheduled'] } },
    { key: 'send', label: '立即发送', permission: 'messages.send', confirm: true, visibleWhen: { field: 'status', in: ['draft', 'scheduled', 'failed'] } },
    { key: 'copy', label: '复制' },
    { key: 'cancel', label: '取消发送', permission: 'messages.cancel', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['scheduled', 'sending'] } },
  ],
  batchActions: [
    { key: 'batch-send', label: '批量发送', permission: 'messages.send', kind: 'task', confirm: true },
    { key: 'batch-cancel', label: '取消发送', permission: 'messages.cancel', tone: 'danger', kind: 'transition', confirm: true },
  ],
  detailSections: [
    { title: '消息内容', fields: ['message_no', 'title', 'message_type', 'template_name', 'channels', 'content'] },
    { title: '受众与送达', fields: ['audience_type', 'audience_label', 'status', 'scheduled_at', 'sent_at', 'delivered', 'read_count', 'failed_count'] },
    { title: '创建信息', fields: ['created_by', 'created_at', 'updated_at'] },
  ],
  detailMetrics: [
    { label: '已送达', field: 'delivered', type: 'number', suffix: ' 人次' },
    { label: '已读', field: 'read_count', type: 'number', suffix: ' 人次' },
    { label: '失败', field: 'failed_count', type: 'number', suffix: ' 人次' },
  ],
  notice: '发送前会再次校验受众范围与模板变量；已发送消息不可修改，只能复制后重新编排。',
  emptyHint: '当前筛选下没有通知消息。',
  seedKey: 'messages',
}

export const notificationTemplatesPage: PageDefinition = {
  id: 'notification-templates',
  route: '#/messages/templates',
  title: '通知模板',
  purpose: '维护多渠道通知模板、变量、跳转链接与启停状态，供消息通知统一复用',
  group: '消息通知',
  icon: 'template',
  kind: 'table',
  permission: 'templates.view',
  permissions: { manage: 'templates.manage' },
  searchKeys: ['name', 'template_type', 'title_template', 'link_name'],
  statusKey: 'status',
  statusMap: TEMPLATE_STATUS,
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '模板总数', kind: 'count' },
    { key: 'active', label: '启用模板', kind: 'count', where: { status: 'active' } },
    { key: 'versions', label: '模板版本', kind: 'sum', field: 'version', suffix: ' 版' },
    { key: 'types', label: '消息类型', kind: 'distinct', field: 'template_type', suffix: ' 类' },
  ],
  columns: [
    { key: 'name', label: '模板名称', type: 'primary', subtitleKey: 'template_type', minWidth: 190, sortable: true },
    { key: 'channels', label: '渠道', type: 'tag', minWidth: 138 },
    { key: 'title_template', label: '标题模板', minWidth: 200 },
    { key: 'variables', label: '变量', type: 'tag', minWidth: 200 },
    { key: 'link_name', label: '跳转目标', minWidth: 130 },
    { key: 'version', label: '版本', type: 'number', width: 64, align: 'center' },
    { key: 'status', label: '状态', type: 'status', width: 80 },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '模板名称或标题', width: 220 },
    { key: 'template_type', label: '消息类型', type: 'select', options: TEMPLATE_TYPES, width: 120 },
    { key: 'channel', label: '渠道', type: 'select', options: CHANNEL_OPTIONS, width: 120 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(TEMPLATE_STATUS), width: 104 },
    { key: 'updated_at', label: '更新时间', type: 'daterange', timeField: 'updated_at', width: 230 },
  ],
  formFields: [
    { key: 'name', label: '模板名称', type: 'text', required: true, maxLength: 40 },
    { key: 'template_type', label: '消息类型', type: 'select', options: TEMPLATE_TYPES, required: true },
    { key: 'channels', label: '适用渠道', type: 'multiselect', options: CHANNEL_OPTIONS, required: true },
    { key: 'title_template', label: '标题模板', type: 'text', required: true, maxLength: 60, placeholder: '支持 {{用户姓名}} 变量' },
    { key: 'content_template', label: '内容模板', type: 'textarea', required: true, full: true, maxLength: 500 },
    { key: 'variables', label: '可用变量', type: 'multiselect', options: MESSAGE_VARIABLES, required: true },
    { key: 'link_name', label: '跳转链接', type: 'select', optionSource: 'links', help: '统一从链接管理选择用户端目标' },
    { key: 'status', label: '状态', type: 'select', options: toOptions(TEMPLATE_STATUS), defaultValue: 'active' },
  ],
  createLabel: '新增模板',
  createPermission: 'templates.manage',
  editPermission: 'templates.manage',
  exportScope: 'none',
  extraActions: [
    { key: 'messages', label: '返回消息通知', icon: 'bell', kind: 'link', to: '#/messages', permission: 'messages.view' },
  ],
  batchActions: [
    { key: 'batch-delete', label: '批量删除', permission: 'templates.manage', tone: 'danger', confirm: true, kind: 'delete' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'templates.manage', tone: 'primary' },
    { key: 'preview', label: '预览模板' },
    { key: 'test-send', label: '测试发送', permission: 'templates.manage', confirm: true },
    { key: 'delete', label: '删除', permission: 'templates.manage', tone: 'danger', confirm: true, confirmText: '仅未被消息或运营位引用的模板可以删除；删除后不可恢复。' },
    { key: 'toggle', label: '停用', permission: 'templates.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['active'] } },
    { key: 'toggle', label: '启用', permission: 'templates.manage', visibleWhen: { field: 'status', in: ['disabled'] } },
  ],
  detailSections: [
    { title: '模板信息', fields: ['name', 'template_type', 'channels', 'title_template', 'content_template'] },
    { title: '变量与链接', fields: ['variables', 'link_name', 'version', 'status', 'created_at', 'updated_at'] },
  ],
  notice: '模板变量必须来自可用变量列表；测试发送只使用当前管理员账号，不触达真实用户。',
  emptyHint: '还没有通知模板。',
  seedKey: 'templates',
}

export const contentTipsPage: PageDefinition = {
  id: 'content-tips',
  route: '#/content/tips',
  title: '健康贴士',
  purpose: '运营用户端首页与健康看板内容，维护封面、适用人群、跳转、排序和上下线状态',
  group: '运营中心',
  icon: 'leaf',
  kind: 'table',
  permission: 'content.view',
  permissions: { manage: 'content.manage', status: 'content.status' },
  searchKeys: ['title', 'category', 'summary', 'audience', 'link_name'],
  statusKey: 'status',
  statusMap: CONTENT_STATUS,
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '内容总数', kind: 'count' },
    { key: 'online', label: '已上线', kind: 'count', where: { status: 'online' } },
    { key: 'scheduled', label: '待上线', kind: 'count', where: { status: 'scheduled' } },
    { key: 'views', label: '累计阅读', kind: 'sum', field: 'views', suffix: ' 次' },
  ],
  columns: [
    { key: 'title', label: '贴士标题', type: 'primary', subtitleKey: 'summary', minWidth: 260, sortable: true },
    { key: 'category', label: '分类', type: 'tag', width: 100 },
    { key: 'audience', label: '适用人群', width: 118 },
    { key: 'link_name', label: '跳转目标', minWidth: 130 },
    { key: 'sort', label: '排序', type: 'number', width: 66, align: 'center' },
    { key: 'status', label: '状态', type: 'status', width: 88 },
    { key: 'views', label: '阅读量', type: 'number', width: 88, align: 'right' },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '标题、摘要或适用人群', width: 230 },
    { key: 'category', label: '内容分类', type: 'select', options: TIP_CATEGORIES, width: 118 },
    { key: 'audience', label: '适用人群', type: 'select', options: AUDIENCES, width: 126 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(CONTENT_STATUS), width: 108 },
    { key: 'updated_at', label: '更新时间', type: 'daterange', timeField: 'updated_at', width: 230 },
  ],
  formFields: [
    { key: 'title', label: '贴士标题', type: 'text', required: true, maxLength: 60 },
    { key: 'category', label: '内容分类', type: 'select', options: TIP_CATEGORIES, required: true },
    { key: 'audience', label: '适用人群', type: 'select', options: AUDIENCES, required: true },
    { key: 'link_name', label: '跳转链接', type: 'select', optionSource: 'links', help: '统一从链接管理选择目标页面' },
    { key: 'summary', label: '摘要', type: 'textarea', required: true, full: true, maxLength: 160 },
    { key: 'content', label: '正文内容', type: 'textarea', required: true, full: true, maxLength: 1000 },
    { key: 'cover_name', label: '封面素材', type: 'select', optionSource: 'assets', help: '来自附件库，封面比例 16:9' },
    { key: 'sort', label: '排序', type: 'number', min: 1, max: 999, defaultValue: 1 },
    { key: 'status', label: '上线状态', type: 'select', options: toOptions(CONTENT_STATUS), defaultValue: 'online' },
  ],
  createLabel: '新增贴士',
  createPermission: 'content.manage',
  editPermission: 'content.manage',
  exportScope: 'none',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'content.manage', tone: 'primary' },
    { key: 'online', label: '上线', permission: 'content.status', visibleWhen: { field: 'status', in: ['offline', 'scheduled'] } },
    { key: 'offline', label: '下线', permission: 'content.status', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['online'] } },
    { key: 'preview', label: '预览' },
    { key: 'delete', label: '删除', permission: 'content.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['offline'] }, confirmText: '仅已下线且未被用户端页面引用的贴士可以删除。' },
  ],
  batchActions: [
    { key: 'batch-online', label: '批量上线', permission: 'content.status', kind: 'transition' },
    { key: 'batch-offline', label: '批量下线', permission: 'content.status', tone: 'danger', kind: 'transition', confirm: true },
  ],
  detailSections: [
    { title: '内容信息', fields: ['title', 'category', 'audience', 'summary', 'content'] },
    { title: '运营配置', fields: ['cover_name', 'link_name', 'sort', 'status', 'views', 'updated_at', 'created_at'] },
  ],
  notice: '下线内容会立即从用户端移除，但不会删除历史阅读数据；封面必须来自附件库。',
  emptyHint: '当前筛选下没有健康贴士。',
  seedKey: 'tips',
}

export const contentFaqPage: PageDefinition = {
  id: 'content-faq',
  route: '#/content/faq',
  title: '帮助中心',
  purpose: '维护帮助分类、常见问题、回答、跳转与发布状态，支撑用户端帮助与反馈入口',
  group: '运营中心',
  icon: 'help',
  kind: 'table',
  permission: 'content.view',
  permissions: { manage: 'content.manage', status: 'content.status' },
  searchKeys: ['question', 'category_name', 'answer', 'link_name'],
  statusKey: 'status',
  statusMap: { published: { value: 'published', label: '已发布', tone: 'success' }, offline: { value: 'offline', label: '已下线', tone: 'neutral' } },
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '问题总数', kind: 'count' },
    { key: 'published', label: '已发布', kind: 'count', where: { status: 'published' } },
    { key: 'offline', label: '已下线', kind: 'count', where: { status: 'offline' } },
    { key: 'views', label: '累计查看', kind: 'sum', field: 'views', suffix: ' 次' },
  ],
  columns: [
    { key: 'question', label: '问题', type: 'primary', subtitleKey: 'answer', minWidth: 300, sortable: true },
    { key: 'category_name', label: '帮助分类', width: 116 },
    { key: 'link_name', label: '跳转目标', minWidth: 130 },
    { key: 'sort', label: '排序', type: 'number', width: 64, align: 'center' },
    { key: 'status', label: '状态', type: 'status', width: 84 },
    { key: 'views', label: '查看量', type: 'number', width: 84, align: 'right' },
    { key: 'published_at', label: '发布时间', type: 'datetime', width: 150 },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '问题、回答或帮助分类', width: 230 },
    { key: 'category_name', label: '帮助分类', type: 'select', optionSource: 'faq_categories', width: 126 },
    { key: 'status', label: '发布状态', type: 'select', options: [
      { value: 'published', label: '已发布' },
      { value: 'offline', label: '已下线' },
    ], width: 108 },
    { key: 'updated_at', label: '更新时间', type: 'daterange', timeField: 'updated_at', width: 230 },
  ],
  formFields: [
    { key: 'question', label: '问题', type: 'text', required: true, maxLength: 80 },
    { key: 'category_name', label: '帮助分类', type: 'select', optionSource: 'faq_categories', required: true },
    { key: 'answer', label: '回答', type: 'textarea', required: true, full: true, maxLength: 1000 },
    { key: 'link_name', label: '延伸链接', type: 'select', optionSource: 'links', help: '可选，统一从链接管理选择' },
    { key: 'sort', label: '排序', type: 'number', min: 1, max: 999, defaultValue: 1 },
    { key: 'status', label: '发布状态', type: 'select', options: [
      { value: 'published', label: '发布' },
      { value: 'offline', label: '下线' },
    ], defaultValue: 'published' },
  ],
  createLabel: '新增问题',
  createPermission: 'content.manage',
  editPermission: 'content.manage',
  exportScope: 'none',
  extraActions: [
    { key: 'categories', label: '帮助分类', icon: 'layers', kind: 'link', to: '#/content/faq?panel=categories', permission: 'content.manage' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'content.manage', tone: 'primary' },
    { key: 'preview', label: '预览帮助页' },
    { key: 'publish', label: '发布', permission: 'content.status', visibleWhen: { field: 'status', in: ['offline'] } },
    { key: 'offline', label: '下线', permission: 'content.status', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['published'] } },
    { key: 'delete', label: '删除', permission: 'content.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['offline'] }, confirmText: '仅下线的帮助问题可以删除；删除后不可恢复。' },
  ],
  detailSections: [
    { title: '帮助内容', fields: ['question', 'category_name', 'answer', 'link_name'] },
    { title: '发布信息', fields: ['sort', 'status', 'views', 'published_at', 'updated_at'] },
  ],
  notice: '下线问题后用户端帮助中心不再展示，但历史访问统计保留。',
  emptyHint: '当前筛选下没有帮助问题。',
  seedKey: 'faq',
}
